<template>
  <AppNavigation mode="topbar"/>
  <div class="faq-container">
    <div class="faq-content">
      <h1>{{ $t("faq.title") }}</h1>

      <section class="faq-item">
        <h2>{{ $t("faq.mobileApp.question") }}</h2>
        <p>{{ $t("faq.mobileApp.answer") }}</p>
        <StoreBadges class="faq-store-badges" />
        <!-- i18n-t rather than a plain $t: the emphasised fragment is markup,
             and the only alternatives are v-html (which the literal-copy
             scanner cannot see through) or losing the emphasis. scope="global"
             because these views are Options API and never call useI18n(), the
             same shape as OnboardingWizard.vue's waitlist banner. -->
        <i18n-t keypath="faq.mobileApp.browser" tag="p" scope="global">
          <template #site><strong>articulearnai.com</strong></template>
        </i18n-t>
      </section>

      <section class="faq-item">
        <h2>{{ $t("faq.homeScreen.question") }}</h2>
        <p>{{ $t("faq.homeScreen.intro") }}</p>
        <h3>{{ $t("faq.iphoneHeading") }}</h3>
        <ol>
          <i18n-t keypath="faq.homeScreen.ios.step1" tag="li" scope="global">
            <template #site><strong>articulearnai.com</strong></template>
          </i18n-t>
          <i18n-t keypath="faq.homeScreen.ios.step2" tag="li" scope="global">
            <template #action><strong>{{ $t("faq.actions.share") }}</strong></template>
          </i18n-t>
          <i18n-t keypath="faq.homeScreen.ios.step3" tag="li" scope="global">
            <template #action><strong>{{ $t("faq.actions.addToHomeScreenIos") }}</strong></template>
          </i18n-t>
          <i18n-t keypath="faq.homeScreen.ios.step4" tag="li" scope="global">
            <template #action><strong>{{ $t("faq.actions.add") }}</strong></template>
          </i18n-t>
          <li>{{ $t("faq.homeScreen.iconAppears") }}</li>
        </ol>
        <h3>{{ $t("faq.androidHeading") }}</h3>
        <ol>
          <i18n-t keypath="faq.homeScreen.android.step1" tag="li" scope="global">
            <template #site><strong>articulearnai.com</strong></template>
          </i18n-t>
          <i18n-t keypath="faq.homeScreen.android.step2" tag="li" scope="global">
            <template #action><strong>{{ $t("faq.actions.threeDotMenu") }}</strong></template>
          </i18n-t>
          <i18n-t keypath="faq.homeScreen.android.step3" tag="li" scope="global">
            <template #action><strong>{{ $t("faq.actions.addToHomeScreenAndroid") }}</strong></template>
          </i18n-t>
          <i18n-t keypath="faq.homeScreen.android.step4" tag="li" scope="global">
            <template #action><strong>{{ $t("faq.actions.add") }}</strong></template>
          </i18n-t>
          <li>{{ $t("faq.homeScreen.iconAppears") }}</li>
        </ol>
      </section>

      <section class="faq-item">
        <h2>{{ $t("faq.microphone.question") }}</h2>
        <p>{{ $t("faq.microphone.intro") }}</p>
        <h3>{{ $t("faq.iphoneHeading") }}</h3>
        <ol>
          <i18n-t keypath="faq.microphone.ios.step1" tag="li" scope="global">
            <template #action><strong>{{ $t("faq.actions.settingsApp") }}</strong></template>
          </i18n-t>
          <li>{{ $t("faq.microphone.ios.step2") }}</li>
          <i18n-t keypath="faq.microphone.ios.step3" tag="li" scope="global">
            <template #action><strong>{{ $t("faq.actions.microphone") }}</strong></template>
          </i18n-t>
          <i18n-t keypath="faq.microphone.ios.step4" tag="li" scope="global">
            <template #action><strong>{{ $t("faq.actions.allow") }}</strong></template>
          </i18n-t>
          <i18n-t keypath="faq.microphone.ios.step5" tag="li" scope="global">
            <template #site><strong>articulearnai.com</strong></template>
          </i18n-t>
        </ol>
        <i18n-t keypath="faq.microphone.note" tag="p" class="faq-note" scope="global">
          <template #label><strong>{{ $t("faq.microphone.noteLabel") }}</strong></template>
        </i18n-t>
        <h3>{{ $t("faq.androidHeading") }}</h3>
        <ol>
          <i18n-t keypath="faq.microphone.android.step1" tag="li" scope="global">
            <template #site><strong>articulearnai.com</strong></template>
          </i18n-t>
          <i18n-t keypath="faq.microphone.android.step2" tag="li" scope="global">
            <template #action><strong>{{ $t("faq.actions.lockIcon") }}</strong></template>
          </i18n-t>
          <i18n-t keypath="faq.microphone.android.step3" tag="li" scope="global">
            <template #action><strong>{{ $t("faq.actions.permissions") }}</strong></template>
            <template #alternative><strong>{{ $t("faq.actions.siteSettings") }}</strong></template>
          </i18n-t>
          <i18n-t keypath="faq.microphone.android.step4" tag="li" scope="global">
            <template #action><strong>{{ $t("faq.actions.microphone") }}</strong></template>
            <template #choice><strong>{{ $t("faq.actions.allow") }}</strong></template>
          </i18n-t>
          <li>{{ $t("faq.microphone.android.step5") }}</li>
        </ol>
      </section>

      <section class="faq-item">
        <h2>{{ $t("faq.tutor.question") }}</h2>
        <p>{{ $t("faq.tutor.answer") }}</p>
      </section>

      <section class="faq-item">
        <h2>{{ $t("faq.desktop.question") }}</h2>
        <i18n-t keypath="faq.desktop.answer" tag="p" scope="global">
          <template #site><strong>articulearnai.com</strong></template>
        </i18n-t>
      </section>

      <section class="faq-item">
        <h2>{{ $t("faq.audio.question") }}</h2>
        <ul>
          <i18n-t keypath="faq.audio.checkPermission" tag="li" scope="global">
            <template #site><strong>articulearnai.com</strong></template>
          </i18n-t>
          <li>{{ $t("faq.audio.otherApps") }}</li>
          <li>{{ $t("faq.audio.refresh") }}</li>
          <li>{{ $t("faq.audio.supportedBrowsers") }}</li>
          <li>{{ $t("faq.audio.iphoneBrowsers") }}</li>
          <i18n-t keypath="faq.audio.contact" tag="li" scope="global">
            <template #email>
              <a href="mailto:articulearnai@gmail.com">articulearnai@gmail.com</a>
            </template>
          </i18n-t>
        </ul>
      </section>
    </div>
  </div>
  <FooterSection/>
</template>

<script>
import AppNavigation from "@/components/AppNavigation.vue";
import FooterSection from "@/components/FooterSection.vue";
import StoreBadges from "@/components/StoreBadges.vue";
import { computed } from "vue";
import { useHead } from "@unhead/vue";
import { authUtils } from "@/scripts/authUtils.js";
import { publicPageHead } from "@/scripts/publicHead.js";

export default {
  name: "FAQView",
  components: { AppNavigation, FooterSection, StoreBadges },
  mounted() {
    authUtils.waitForAuthInitialization();
    // computed, not a plain string: locale bundles load lazily, so a value read
    // at mount can freeze the English fallback into the tab and the og: card.
    // No htmlAttrs.lang anywhere in here: <html lang> is owned by the locale
    // watcher in src/locales/index.js and describes the interface the reader is
    // using. Two writers would race, and the loser would be whichever ran last
    // on that particular navigation.
    useHead(
      publicPageHead({
        title: computed(() => this.$t("navigation.faq")),
        description: computed(() => this.$t("faq.metaDescription")),
        path: "/faq",
      }),
    );
  },
};
</script>

<style scoped>
.faq-container {
  display: flex;
  justify-content: center;
  padding: 2rem 1rem 4rem;
}

.faq-content {
  max-width: 680px;
  width: 100%;
  color: var(--al-gray-700);
  line-height: 1.7;
}

.faq-content h1 {
  font-size: 2rem;
  margin-bottom: 2rem;
  color: var(--al-ink-900);
}

.faq-item {
  border-top: 1px solid var(--al-gray-300);
  padding-top: 2.5rem;
  margin-top: 2.5rem;
}

.faq-item:first-of-type {
  border-top: none;
  padding-top: 0;
  margin-top: 0;
}

.faq-item h2 {
  font-size: 1.3rem;
  font-weight: 700;
  margin-bottom: 1rem;
  color: var(--al-ink-900);
}

.faq-item h3 {
  font-size: 1rem;
  font-weight: 700;
  color: var(--al-primary-hover);
  margin-top: 1.25rem;
  margin-bottom: 0.5rem;
}

.faq-content p,
.faq-content li {
  font-size: 0.95rem;
}

.faq-content ol,
.faq-content ul {
  padding-left: 1.5rem;
  margin-bottom: 1rem;
}

.faq-content a {
  color: var(--al-primary-hover);
}

.faq-store-badges {
  align-items: flex-start;
  margin: 1.25rem 0;
}

.faq-note {
  background-color: color-mix(in srgb, var(--al-orange) 12%, var(--al-white));
  border-left: 3px solid var(--al-orange);
  padding: 0.6rem 0.9rem;
  border-radius: 0 4px 4px 0;
  margin: 0.75rem 0;
}

@media (max-width: 767px) {
  .faq-container {
    padding: 1.25rem 1.25rem 3rem;
  }

  .faq-content h1 {
    font-size: 1.5rem;
    margin-bottom: 1.5rem;
  }

  .faq-item {
    padding-top: 1.75rem;
    margin-top: 1.75rem;
  }

  .faq-item h2 {
    font-size: 1.1rem;
  }
}
</style>

